import type { Prospect } from '@clareefai/types';
import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  Building, Calendar,
  CheckCircle, Clock,
  Mail, Pencil, Phone,
  RefreshCw,
  Trash2,
  User,
  UserCog,
  Users
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, toast } from '@clareefai/ui';
import PageTitle from '../../components/shared/PageTitle';
import AssignSalespersonModal from '../../components/prospects/AssignSalespersonModal';
import EditProspectModal from '../../components/prospects/EditProspectModal';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import { FirstLoginChip, InvitedByLine } from '../../components/shared/ProfileMeta';
import ProspectMeetingSchedulerModal from '../../components/prospects/ProspectMeetingSchedulerModal';
import RescheduleForm from '../../components/prospects/RescheduleForm';
import SlideModal from '../../components/shared/SlideModal';
import { useAbility } from '../../contexts/AbilityContext';
import { useGetVendorMeetingsQuery } from '../../store/meetingApi';
import { useGetPromotersQuery } from '../../store/promoterApi';
import { useGetProspectByIdQuery, useDeleteProspectMutation, useResendProspectInvitationMutation } from '../../store/prospectApi';
import { useGetTeamMembersQuery } from '../../store/teamMemberApi';

type TabType = 'promoters' | 'requested' | 'upcoming' | 'history' | 'rescheduled';

const ProspectDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ability = useAbility();
  const canEditProspect = ability.can('update', 'Prospect');
  const canDeleteProspect = ability.can('delete', 'Prospect');
  const { data: prospectData, isLoading: prospectLoading, error: prospectError, refetch } = useGetProspectByIdQuery(id || '');
  const { data: promotersData } = useGetPromotersQuery();
  const { data: meetingsData } = useGetVendorMeetingsQuery();
  const { data: teamMembers = [] } = useGetTeamMembersQuery();
  const [deleteProspect, { isLoading: isDeleting }] = useDeleteProspectMutation();
  const [resendInvitation, { isLoading: isResending }] = useResendProspectInvitationMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [showAssignSalesperson, setShowAssignSalesperson] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('promoters');

  // Filter meetings for this prospect
  const prospectMeetings = useMemo(() => {
    if (!meetingsData || !id) return [];
    return meetingsData.filter(m => m.prospect_id === id);
  }, [meetingsData, id]);

  // Convert prospect data to frontend format
  const prospect = useMemo(() => {
    if (!prospectData) return null;
    const assignedPromoters = prospectData.assignedPromoters?.length
      ? prospectData.assignedPromoters
      : (prospectData.promoterId ? [prospectData.promoterId] : []);

    return {
      id: prospectData.id || '',
      name: `${prospectData.firstName} ${prospectData.lastName}`,
      email: prospectData.email,
      phone: prospectData.phone || '',
      company: prospectData.company || '',
      promoterId: prospectData.promoterId || '',
      promoterName: prospectData.promoterName || '',
      assignedPromoters,
      status: prospectData.status as Prospect['status'],
      assignedSalespersonId: prospectData.assignedSalespersonId ?? undefined,
      notes: prospectData.notes ?? undefined,
      sectors: prospectData.sectors,
      companySize: prospectData.companySize ?? undefined,
      createdDate: prospectData.createdAt || new Date().toISOString(),
      assignedTo: undefined,
    };
  }, [prospectData]);

  // Convert promoters data to frontend format
  const promoters = useMemo(() => {
    if (!promotersData) return [];
    return promotersData.map((promoter) => ({
      id: promoter.id,
      name: `${promoter.firstName} ${promoter.lastName}`,
      email: promoter.email,
      phone: promoter.phone || '',
      company: promoter.company || '',
      avatar: promoter.avatar,
      status: 'available' as const,
      prospectsReferred: 0,
      conversionRate: 0,
      totalRewards: 0,
      joinedDate: promoter.createdAt,
    }));
  }, [promotersData]);

  const selectedPromoters = useMemo(() => {
    if (!prospect || !promoters) return [];
    return promoters.filter(p =>
      prospect.assignedPromoters?.includes(p.id)
    );
  }, [prospect, promoters]);

  // Get past meetings (completed meetings) - MUST be before early returns (Rules of Hooks)
  const pastMeetings = useMemo(() => {
    if (!prospectMeetings) return [];
    return prospectMeetings
      .filter(m => m.status === 'completed')
      .map(m => ({
        id: m.id,
        promoterName: m.promoter_name || m.promoterName || 'Unknown',
        date: m.date || m.accepted_date?.date || '',
        time: m.time || m.accepted_date?.time || '',
        outcome: m.outcome || 'Completed'
      }));
  }, [prospectMeetings]);

  if (prospectLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('common.loading')}</h2>
          <button onClick={() => navigate('/prospects')} className="btn-primary">
            {t('prospects.backToProspects')}
          </button>
        </div>
      </div>
    );
  }

  if (prospectError || !prospect) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('prospects.notFound')}</h2>
          <button onClick={() => navigate('/prospects')} className="btn-primary">
            {t('prospects.backToProspects')}
          </button>
        </div>
      </div>
    );
  }

  // Get meeting requests for a promoter (meetings with requested dates)
  const getMeetingRequestsForPromoter = (promoterId: string) => {
    return prospectMeetings
      .filter(m => m.promoter_id === promoterId && m.status === 'request' && m.requested_dates && m.requested_dates.length > 0)
      .flatMap(m => m.requested_dates.map(date => ({
        id: date.id,
        date: date.date,
        time: date.time,
        status: date.status,
        meetingId: m.id
      })));
  };

  // Get upcoming meetings for a promoter (meetings with accepted date and status booked)
  const getUpcomingMeetingsForPromoter = (promoterId: string) => {
    const meeting = prospectMeetings.find(m =>
      m.promoter_id === promoterId &&
      m.status === 'booked' &&
      m.accepted_date
    );
    if (!meeting || !meeting.accepted_date) return null;
    return {
      id: meeting.id,
      date: meeting.accepted_date.date,
      time: meeting.accepted_date.time,
      type: meeting.type || 'Meeting'
    };
  };

  // Get rescheduled dates for a promoter
  const getRescheduledDatesForPromoter = (promoterId: string) => {
    return prospectMeetings
      .filter(m => m.promoter_id === promoterId && m.rescheduled_dates && m.rescheduled_dates.length > 0)
      .flatMap(m => m.rescheduled_dates.map(date => ({
        id: date.id,
        date: date.date,
        time: date.time,
        status: date.status,
        meetingId: m.id
      })));
  };

  const handleValidateDate = (promoterId: string, dateId: string) => {
    console.log('Validating date', dateId, 'for promoter', promoterId);
  };

  const handleDelete = async () => {
    try {
      await deleteProspect(prospect.id).unwrap();
      toast.success(t('prospects.deletedSuccess'));
      setShowDeleteModal(false);
      navigate('/prospects');
    } catch (err: any) {
      toast.error(err?.data?.message || t('prospects.deleteFailed'));
    }
  };

  const handleAssignSalesperson = (salespersonId: string, salespersonName: string) => {
    console.log('Assigning salesperson:', salespersonId, salespersonName);
  };

  const assignedSalesperson = prospect.assignedSalespersonId
    ? teamMembers.find(m => m.id === prospect.assignedSalespersonId)
    : null;

  const tabs = [
    { id: 'promoters' as TabType, label: t('prospects.tabs.promoters'), icon: Users },
    { id: 'requested' as TabType, label: t('prospects.tabs.requested'), icon: Calendar },
    { id: 'upcoming' as TabType, label: t('prospects.tabs.upcoming'), icon: CheckCircle },
    { id: 'history' as TabType, label: t('prospects.tabs.history'), icon: Clock },
    { id: 'rescheduled' as TabType, label: t('prospects.tabs.rescheduled'), icon: RefreshCw }
  ];

  return (
    <>
      <PageTitle title={prospect ? `${prospect.name} · Prospect` : 'Prospect'} />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="bg-gradient-to-r from-[#4339A0] via-[#5a4dba] to-[#3d2f8f] text-white">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <button
              onClick={() => navigate('/prospects')}
              className="mb-6 flex items-center space-x-2 text-[#b8b0f0] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>{t('prospects.backToProspects')}</span>
            </button>

            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
              <div className="flex items-start space-x-6">
                <div className="relative">
                  <div className="w-32 h-32 bg-white/10 backdrop-blur-sm border-4 border-white rounded-2xl flex items-center justify-center shadow-xl">
                    <User className="w-16 h-16 text-white" />
                  </div>
                  <div className="absolute -bottom-2 -right-2 bg-yellow-400 text-yellow-900 p-2 rounded-lg shadow-lg">
                    <CheckCircle className="w-6 h-6" />
                  </div>
                </div>

                <div>
                  <h1 className="text-4xl font-bold mb-2">{prospect.name}</h1>
                  <p className="text-xl text-[#b8b0f0] mb-3">{prospect.company}</p>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-sm font-semibold rounded-full">
                      {prospect.status.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    <FirstLoginChip loggedIn={prospectData?.hasLoggedIn} />
                    {prospect.sectors && prospect.sectors.map((sector, index) => (
                      <span key={index} className="px-3 py-1 bg-[#4339A0]/30 backdrop-blur-sm text-white text-sm font-medium rounded-full">
                        {sector}
                      </span>
                    ))}
                  </div>

                  {prospectData?.invitedBy && (
                    <InvitedByLine invitedBy={prospectData.invitedBy} className="text-white mb-4" />
                  )}

                  <div className="flex gap-3">
                    <a href={`mailto:${prospect.email}`} className="text-[#b8b0f0] hover:text-white transition-colors">
                      <Mail className="w-5 h-5" />
                    </a>
                    {prospect.phone && (
                      <a href={`tel:${prospect.phone}`} className="text-[#b8b0f0] hover:text-white transition-colors">
                        <Phone className="w-5 h-5" />
                      </a>
                    )}
                  </div>

                  {(canEditProspect || canDeleteProspect) && (
                    <div className="flex items-center gap-3 mt-4">
                      {canEditProspect && (
                        <button
                          onClick={() => setShowEditModal(true)}
                          className="flex items-center space-x-1.5 px-4 py-1.5 bg-white/20 hover:bg-white/30 text-white text-sm rounded-lg transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                          <span>{t('common.edit')}</span>
                        </button>
                      )}
                      {canEditProspect && (
                        <button
                          onClick={async () => {
                            if (!id) return;
                            try {
                              await resendInvitation(id).unwrap();
                              toast.success(t('prospects.magicLinkSent'));
                            } catch {
                              toast.error(t('prospects.magicLinkFailed'));
                            }
                          }}
                          disabled={isResending}
                          className="flex items-center space-x-1.5 px-4 py-1.5 bg-white/20 hover:bg-white/30 disabled:opacity-60 text-white text-sm rounded-lg transition-colors"
                        >
                          <RefreshCw className={`w-4 h-4 ${isResending ? 'animate-spin' : ''}`} />
                          <span>{isResending ? t('common.sending') : t('prospects.sendMagicLink')}</span>
                        </button>
                      )}
                      {canDeleteProspect && (
                        <button
                          onClick={() => setShowDeleteModal(true)}
                          className="flex items-center space-x-1.5 px-4 py-1.5 bg-red-600/80 hover:bg-red-700 text-white text-sm rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>{t('common.delete')}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4 lg:min-w-[400px]">
                <div
                  onClick={() => setShowAssignSalesperson(true)}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/20 transition-all cursor-pointer group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <UserCog className="w-8 h-8 text-[#9d8edb]" />
                      <div className="text-sm text-[#b8b0f0]">{t('prospects.assignedSalesperson')}</div>
                    </div>
                    <button className="text-[#b8b0f0] hover:text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <UserCog className="w-5 h-5" />
                    </button>
                  </div>
                  {assignedSalesperson ? (
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white font-bold">
                        {assignedSalesperson.avatar}
                      </div>
                      <div>
                        <div className="text-base font-semibold">{assignedSalesperson.name}</div>
                        <div className="text-sm text-[#b8b0f0]">{assignedSalesperson.role}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-base font-semibold text-yellow-300 flex items-center space-x-2">
                      <AlertTriangle className="w-5 h-5" />
                      <span>{t('prospects.clickToAssign')}</span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                    <Building className="w-8 h-8 text-[#9d8edb] mb-2" />
                    <div className="text-sm text-[#b8b0f0] mb-1">{t('prospects.companySize')}</div>
                    <div className="text-base font-semibold">{prospect.companySize || 'N/A'}</div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                    <Calendar className="w-8 h-8 text-[#9d8edb] mb-2" />
                    <div className="text-sm text-[#b8b0f0] mb-1">{t('prospects.created')}</div>
                    <div className="text-base font-semibold">{new Date(prospect.createdDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-8 overflow-hidden sticky top-0 z-10">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-6 py-4 font-medium transition-all whitespace-nowrap ${isActive
                        ? 'text-[#4339A0] border-b-2 border-[#4339A0] bg-[#f5f3ff]'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-6">
            {activeTab === 'promoters' && (
              <div>
                {selectedPromoters.length === 0 ? (
                  <div className="text-center py-20 text-gray-500">
                    <Users className="w-20 h-20 mx-auto mb-4 text-gray-300" />
                    <p className="text-xl font-medium text-gray-900 mb-2">{t('prospects.noPromotersAssigned')}</p>
                    <p className="text-gray-600">{t('prospects.noPromotersAssignedHelp')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {selectedPromoters.map((promoter) => (
                      <div
                        key={promoter.id}
                        onClick={() => navigate(`/promoters/${promoter.id}`)}
                        className="group border-2 border-gray-200 rounded-xl p-6 hover:shadow-xl transition-all hover:border-[#4339A0] hover:bg-[#f5f3ff] cursor-pointer"
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="relative mb-4">
                            <Avatar
                              src={promoter.avatar}
                              name={promoter.name}
                              size="lg"
                              className="w-20 h-20 border-4 border-white shadow-md group-hover:border-[#4339A0] transition-all"
                            />
                            <div className="absolute inset-0 rounded-full bg-[#4339A0]/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <h3 className="font-bold text-lg text-gray-900 mb-1 group-hover:text-[#4339A0] transition-colors">{promoter.name}</h3>
                          <p className="text-sm text-gray-600 mb-3">{promoter.company}</p>
                          <div className="w-full pt-3 border-t border-gray-200 group-hover:border-[#4339A0] transition-colors">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-500">{t('prospects.prospectsCount')}</span>
                              <span className="text-sm font-semibold text-gray-900 group-hover:text-[#4339A0] transition-colors">{promoter.prospectsReferred}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">{t('prospects.conversion')}</span>
                              <span className="text-sm font-semibold text-green-600 group-hover:text-[#4339A0] transition-colors">{promoter.conversionRate}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'requested' && (
              <div>
                <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-sm text-blue-800">
                    {t('prospects.requestedInfo')}
                  </p>
                </div>
                {selectedPromoters.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Calendar className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                    <p>{t('prospects.noPromotersAssigned')}</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {selectedPromoters.map((promoter) => {
                      const requests = getMeetingRequestsForPromoter(promoter.id);
                      return (
                        <div key={promoter.id} className="border border-gray-200 rounded-lg p-5">
                          <div className="flex items-center space-x-3 mb-4">
                            <Avatar
                              src={promoter.avatar}
                              name={promoter.name}
                              size="md"
                            />
                            <div>
                              <h3 className="font-semibold text-gray-900">{promoter.name}</h3>
                              <p className="text-sm text-gray-600">{promoter.company}</p>
                            </div>
                          </div>
                          {requests.length === 0 ? (
                            <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                              <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                              <p className="text-sm">{t('prospects.noDateProposals')}</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {requests.map((meeting, index) => (
                                <div key={meeting.id} className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-lg p-4">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm">
                                      {index + 1}
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900">
                                        {new Date(meeting.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                      </p>
                                      <p className="text-sm text-gray-600">{t('prospects.at')} {meeting.time}</p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleValidateDate(promoter.id, meeting.id)}
                                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                                  >
                                    {t('prospects.validate')}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'upcoming' && (
              <div>
                <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-sm text-green-800">
                    {t('prospects.upcomingInfo')}
                  </p>
                </div>
                {selectedPromoters.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <CheckCircle className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                    <p>{t('prospects.noPromotersAssigned')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedPromoters.map((promoter) => {
                      const meeting = getUpcomingMeetingsForPromoter(promoter.id);
                      return (
                        <div key={promoter.id} className="border border-gray-200 rounded-lg p-5">
                          <div className="flex items-center space-x-3 mb-4">
                            <Avatar
                              src={promoter.avatar}
                              name={promoter.name}
                              size="md"
                            />
                            <div>
                              <h3 className="font-semibold text-gray-900">{promoter.name}</h3>
                              <p className="text-sm text-gray-600">{promoter.company}</p>
                            </div>
                          </div>
                          {!meeting ? (
                            <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                              <p className="text-sm">{t('prospects.noValidatedMeeting')}</p>
                            </div>
                          ) : (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-semibold text-gray-900">{meeting.type}</p>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {new Date(meeting.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} {t('prospects.at')} {meeting.time}
                                  </p>
                                </div>
                                <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                                  {t('prospects.confirmed')}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div>
                <div className="mb-8 p-4 bg-gray-50 border border-gray-200 rounded-xl">
                  <p className="text-sm text-gray-700">
                    {t('prospects.historyInfo')}
                  </p>
                </div>
                {pastMeetings.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Clock className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                    <p className="text-lg font-medium">{t('prospects.noMeetingHistory')}</p>
                    <p className="text-sm">{t('prospects.noMeetingsCompleted')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pastMeetings.map((meeting) => (
                      <div key={meeting.id} className="border border-gray-200 rounded-lg p-5 bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-gray-900">{meeting.promoterName}</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              {new Date(meeting.date).toLocaleDateString('en-US')} {t('prospects.at')} {meeting.time}
                            </p>
                            <p className="text-sm text-gray-700 mt-2">{meeting.outcome}</p>
                          </div>
                          <span className="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded-full">
                            {t('common.completed')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'rescheduled' && (
              <div>
                <div className="mb-8 p-4 bg-orange-50 border border-orange-200 rounded-xl">
                  <p className="text-sm text-orange-800">
                    {t('prospects.rescheduledInfo')}
                  </p>
                </div>
                {selectedPromoters.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <RefreshCw className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                    <p>{t('prospects.noPromotersAssigned')}</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {selectedPromoters.map((promoter) => {
                      const rescheduled = getRescheduledDatesForPromoter(promoter.id);
                      return (
                        <div key={promoter.id} className="border border-gray-200 rounded-lg p-5">
                          <div className="flex items-center space-x-3 mb-4">
                            <Avatar
                              src={promoter.avatar}
                              name={promoter.name}
                              size="md"
                            />
                            <div>
                              <h3 className="font-semibold text-gray-900">{promoter.name}</h3>
                              <p className="text-sm text-gray-600">{promoter.company}</p>
                            </div>
                          </div>
                          {rescheduled.length === 0 ? (
                            <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                              <RefreshCw className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                              <p className="text-sm">{t('prospects.noRescheduledDates')}</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                                <p className="text-sm text-blue-800">
                                  {t('prospects.rescheduledNote')}
                                </p>
                              </div>
                              {rescheduled.map((meeting, index) => (
                                <div key={meeting.id} className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                                      {index + 1}
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900">
                                        {new Date(meeting.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                      </p>
                                      <p className="text-sm text-gray-600">{t('prospects.at')} {meeting.time}</p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleValidateDate(promoter.id, meeting.id)}
                                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                                  >
                                    {t('prospects.accept')}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={t('prospects.deleteTitle')}
        description={t('prospects.deleteConfirm', { name: prospect.name })}
        confirmLabel={t('prospects.deletePermanently')}
        variant="danger"
        isLoading={isDeleting}
      />

      {showEditModal && prospectData && (
        <EditProspectModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            refetch();
          }}
          prospect={{
            id: prospectData.id,
            email: prospectData.email,
            firstName: prospectData.firstName,
            lastName: prospectData.lastName,
            jobTitle: prospectData.jobTitle,
            phone: prospectData.phone,
            company: prospectData.company,
            companySize: prospectData.companySize,
            sectors: prospectData.sectors,
            notes: prospectData.notes,
            source: prospectData.source,
            status: prospectData.status,
          }}
        />
      )}

      {showArchiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <Archive className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('prospects.archiveTitle')}</h3>
                <p className="text-gray-600">{t('prospects.archiveMoveTo')}</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              {t('prospects.archiveConfirm', { name: prospect.name })}
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowArchiveModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  console.log('Archiving');
                  setShowArchiveModal(false);
                }}
                className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                {t('prospects.archiveTitle')}
              </button>
            </div>
          </div>
        </div>
      )}

      <SlideModal
        isOpen={showRescheduleModal}
        onClose={() => setShowRescheduleModal(false)}
        title={t('prospects.followUpTitle')}
      >
        <RescheduleForm
          prospect={prospect}
          onClose={() => setShowRescheduleModal(false)}
        />
      </SlideModal>

      <ProspectMeetingSchedulerModal
        prospect={prospect}
        isOpen={showScheduler}
        onClose={() => setShowScheduler(false)}
      />

      {showAssignSalesperson && (
        <AssignSalespersonModal
          currentSalespersonId={prospect.assignedSalespersonId ?? undefined}
          onAssign={handleAssignSalesperson}
          onClose={() => setShowAssignSalesperson(false)}
        />
      )}
    </>
  );
};

export default ProspectDetail;
